export const MAX_ROWS = 1_048_576;
export const MAX_COLS = 16_384;

/** One axis (rows or columns): a default size plus a handful of overridden or hidden entries.
 *  Offsets are computed, never stored per index, so a million rows cost nothing. */
export class Axis {
    private ov: number[] = [];
    private sizes = new Map<number, number>();
    /** cum[k] = sum of (size - default) over the first k overrides. */
    private cum: number[] = [0];

    constructor(readonly count: number, readonly def: number) {}

    static from(count: number, def: number, overrides: Record<string, number>, hidden: number[]): Axis {
        const a = new Axis(count, def);
        for (const [k, v] of Object.entries(overrides)) a.sizes.set(Number(k), v);
        for (const h of hidden) a.sizes.set(h, 0);
        a.rebuild();
        return a;
    }

    private rebuild() {
        this.ov = [...this.sizes.keys()].filter((i) => i < this.count).sort((a, b) => a - b);
        this.cum = [0];
        for (const i of this.ov) this.cum.push(this.cum[this.cum.length - 1]! + (this.sizes.get(i)! - this.def));
    }

    set(i: number, size: number) {
        this.sizes.set(i, size);
        this.rebuild();
    }

    size(i: number): number {
        return this.sizes.get(i) ?? this.def;
    }

    hidden(i: number): boolean {
        return this.size(i) === 0;
    }

    /** Pixel offset of the start of index i. */
    offset(i: number): number {
        let lo = 0;
        let hi = this.ov.length;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (this.ov[mid]! < i) lo = mid + 1;
            else hi = mid;
        }
        return i * this.def + this.cum[lo]!;
    }

    get total(): number {
        return this.offset(this.count);
    }

    /** The index whose extent contains px (clamped to the last index). */
    indexAt(px: number): number {
        if (px <= 0) return this.firstVisible();
        let lo = 0;
        let hi = this.count - 1;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (this.offset(mid + 1) > px) hi = mid;
            else lo = mid + 1;
        }
        return lo;
    }

    firstVisible(): number {
        let i = 0;
        while (i < this.count - 1 && this.hidden(i)) i++;
        return i;
    }

    /** Nearest visible index at or beyond i in direction dir (-1 / +1), staying in range. */
    visibleFrom(i: number, dir: 1 | -1): number {
        let j = Math.min(Math.max(i, 0), this.count - 1);
        while (j > 0 && j < this.count - 1 && this.hidden(j)) j += dir;
        return j;
    }
}

/** 0 -> A, 25 -> Z, 26 -> AA. */
export function colName(col: number): string {
    let n = col + 1;
    let s = "";
    while (n > 0) {
        const r = (n - 1) % 26;
        s = String.fromCharCode(65 + r) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
}

export function parseColName(name: string): number | null {
    if (!/^[A-Za-z]{1,3}$/.test(name)) return null;
    let n = 0;
    for (const ch of name.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
    return n - 1 < MAX_COLS ? n - 1 : null;
}

export const cellName = (row: number, col: number) => `${colName(col)}${row + 1}`;

/** "B3" or "B3:D9" or a single column/row ("C:C", "4:4") -> [r0,c0,r1,c1]. */
export function parseRef(text: string): [number, number, number, number] | null {
    const t = text.trim().replace(/\$/g, "");
    const cell = (s: string): [number, number] | null => {
        const m = /^([A-Za-z]{1,3})(\d{1,7})$/.exec(s);
        if (!m) return null;
        const c = parseColName(m[1]!);
        const r = Number(m[2]) - 1;
        return c === null || r < 0 || r >= MAX_ROWS ? null : [r, c];
    };
    const [a, b] = t.split(":");
    if (b === undefined) {
        const p = cell(a!);
        return p ? [p[0], p[1], p[0], p[1]] : null;
    }
    const p = cell(a!);
    const q = cell(b);
    if (p && q) return [Math.min(p[0], q[0]), Math.min(p[1], q[1]), Math.max(p[0], q[0]), Math.max(p[1], q[1])];
    const ca = parseColName(a!);
    const cb = parseColName(b);
    if (ca !== null && cb !== null) return [0, Math.min(ca, cb), MAX_ROWS - 1, Math.max(ca, cb)];
    if (/^\d+$/.test(a!) && /^\d+$/.test(b)) {
        const ra = Number(a) - 1;
        const rb = Number(b) - 1;
        if (ra >= 0 && rb >= 0 && ra < MAX_ROWS && rb < MAX_ROWS) return [Math.min(ra, rb), 0, Math.max(ra, rb), MAX_COLS - 1];
    }
    return null;
}

export type Rect = [number, number, number, number];

export const rectOf = (ar: number, ac: number, br: number, bc: number): Rect => [Math.min(ar, br), Math.min(ac, bc), Math.max(ar, br), Math.max(ac, bc)];

/** CSS colour from the engine's 0xAARRGGBB. */
export function argbCss(argb: number | null | undefined): string | null {
    if (argb === null || argb === undefined) return null;
    const r = (argb >> 16) & 255;
    const g = (argb >> 8) & 255;
    const b = argb & 255;
    return `rgb(${r},${g},${b})`;
}

export function cssToArgb(hex: string): number {
    const n = parseInt(hex.replace("#", ""), 16);
    return (0xff000000 | n) >>> 0;
}
